import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const ThemeContext = createContext(null);
const STORAGE_KEY = "bauheld_panel_theme";

const themes = {
  light: {
    name: "light",
    page: "bg-white text-slate-900 antialiased",
    surface: "bg-white/80 backdrop-blur-sm border border-slate-200/60 text-slate-900 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]",
    surfaceSoft: "bg-slate-50/70 backdrop-blur-sm border border-slate-200/40 text-slate-800",
    header: "bg-white/60 backdrop-blur-2xl border-b border-white/30 text-slate-900 shadow-sm",
    sidebar: "bg-white/90 backdrop-blur-sm text-slate-900 border-r border-slate-200/60 shadow-[2px_0_12px_-4px_rgba(0,0,0,0.05)]",
    input: "bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:ring-2 focus:ring-slate-400/20 rounded-lg transition-all duration-200",
    muted: "text-slate-500 text-sm font-medium tracking-wide",
    buttonPrimary: "bg-slate-900 text-white hover:bg-slate-800 px-4 py-2 rounded-lg font-medium shadow-sm hover:shadow transition-all duration-200 active:scale-[0.98]",
    buttonSecondary: "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 px-4 py-2 rounded-lg font-medium transition-all duration-200 active:scale-[0.98]",
    card: "bg-white/90 backdrop-blur-sm border border-slate-200/60 rounded-xl shadow-lg",
    badge: "bg-slate-100 text-slate-700 px-2.5 py-0.5 rounded-full text-xs font-medium",
    divider: "border-slate-200/60",
  },

  dark: {
    name: "dark",
    page: "bg-[#0A0F1C] text-slate-200 antialiased",
    surface: "bg-[#141C2C]/90 backdrop-blur-sm border border-slate-700/30 text-slate-200 shadow-[0_8px_20px_-8px_rgba(0,0,0,0.5)]",
    surfaceSoft: "bg-[#1E2842]/70 backdrop-blur-sm border border-slate-700/20 text-slate-300",
    header: "bg-[#0A0F1C]/60 backdrop-blur-2xl border-b border-slate-700/30 text-slate-200 shadow-lg",
    sidebar: "bg-[#050914] text-slate-400 border-r border-slate-800/50 shadow-[2px_0_12px_-4px_rgba(0,0,0,0.7)]",
    input: "bg-[#1E2842] border-slate-700/50 text-slate-200 placeholder:text-slate-500 focus:border-slate-500 focus:ring-2 focus:ring-slate-500/20 rounded-lg transition-all duration-200",
    muted: "text-slate-500 text-sm font-medium tracking-wide",
    buttonPrimary: "bg-slate-200 text-slate-900 hover:bg-white px-4 py-2 rounded-lg font-medium shadow-lg transition-all duration-200 active:scale-[0.98]",
    buttonSecondary: "bg-[#1E2842] border border-slate-700/50 text-slate-300 hover:bg-[#25324F] hover:text-slate-200 px-4 py-2 rounded-lg font-medium transition-all duration-200 active:scale-[0.98]",
    card: "bg-[#141C2C]/90 backdrop-blur-sm border border-slate-700/30 rounded-xl shadow-2xl",
    badge: "bg-[#25324F] text-slate-300 px-2.5 py-0.5 rounded-full text-xs font-medium border border-slate-700/30",
    divider: "border-slate-700/30",
  },

  blue: {
    name: "blue",
    // Sfond shumë i dukshëm ngjyrë blu e hapur
    page: "bg-blue-100 text-blue-950 antialiased",
    
    // Sipërfaqet janë lehtësisht të kaltra
    surface: "bg-white/90 backdrop-blur-md border border-blue-300 text-blue-900 shadow-lg shadow-blue-500/15",
    
    surfaceSoft: "bg-blue-200/50 backdrop-blur-md border border-blue-300/80 text-blue-900",
    
    header: "bg-blue-100/90 backdrop-blur-2xl border-b border-blue-300 text-blue-950 shadow-sm",
    
    // Sidebar shumë i dallueshëm
    sidebar: "bg-blue-50 text-blue-900 border-r border-blue-300 shadow-xl",
    
    input: "bg-white border-blue-400 text-blue-950 placeholder:text-blue-500 focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30 rounded-lg transition-all duration-200 shadow-inner",
    
    muted: "text-blue-700 text-sm font-medium tracking-wide",
    
    buttonPrimary: "bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-lg font-semibold shadow-md shadow-blue-500/40 transition-all duration-200 active:scale-[0.98]",
    
    buttonSecondary: "bg-blue-100 border border-blue-400 text-blue-800 hover:bg-blue-200 px-4 py-2 rounded-lg font-medium shadow-sm transition-all duration-200",
    
    card: "bg-white/90 backdrop-blur-md border border-blue-300 rounded-xl shadow-xl shadow-blue-500/10",
    badge: "bg-blue-200 text-blue-800 px-2.5 py-0.5 rounded-full text-xs font-semibold border border-blue-400",
    divider: "border-blue-300",
    accent: "text-blue-600",
    link: "text-blue-700 hover:text-blue-800 font-medium transition-colors",
  },

  ocean: {
    name: "ocean",
    page: "bg-gradient-to-br from-cyan-200 via-blue-200 to-indigo-200/70 text-cyan-950 antialiased",
    surface: "bg-white/85 backdrop-blur-md border border-cyan-300/70 text-cyan-950 shadow-lg shadow-cyan-600/20",
    surfaceSoft: "bg-cyan-100/80 backdrop-blur-md border border-cyan-300/50 text-cyan-900",
    header: "bg-cyan-50/80 backdrop-blur-2xl border-b border-cyan-300/60 text-cyan-950 shadow-sm shadow-cyan-600/10",
    sidebar: "bg-gradient-to-b from-cyan-100 via-blue-100 to-indigo-100 text-cyan-900 border-r border-cyan-300/70 shadow-[2px_0_15px_-3px_rgba(6,182,212,0.25)]",
    input: "bg-white/90 border-cyan-300 text-cyan-950 placeholder:text-cyan-500 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/30 rounded-lg transition-all duration-200 shadow-inner",
    muted: "text-cyan-700 text-sm font-medium tracking-wide",
    buttonPrimary: "bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-600 hover:to-blue-700 px-4 py-2 rounded-lg font-semibold shadow-md shadow-cyan-500/40 transition-all duration-200 active:scale-[0.98] hover:shadow-lg",
    buttonSecondary: "bg-cyan-50/80 border border-cyan-300 text-cyan-700 hover:bg-cyan-100 hover:border-cyan-400 px-4 py-2 rounded-lg font-medium shadow-sm transition-all duration-200 active:scale-[0.98]",
    card: "bg-white/85 backdrop-blur-md border border-cyan-300/70 rounded-xl shadow-xl shadow-cyan-600/20",
    badge: "bg-cyan-100 text-cyan-800 px-2.5 py-0.5 rounded-full text-xs font-semibold border border-cyan-300/60",
    divider: "border-cyan-300/60",
    accent: "text-cyan-600",
    link: "text-cyan-700 hover:text-cyan-800 font-medium transition-colors",
  },

  frost: {
    name: "frost",
    page: "bg-gradient-to-br from-blue-100 via-indigo-50 to-sky-50 text-blue-950 antialiased",
    surface: "bg-white/85 backdrop-blur-md border border-blue-200/70 text-blue-950 shadow-lg shadow-blue-500/10",
    surfaceSoft: "bg-blue-100/60 backdrop-blur-md border border-blue-200/50 text-blue-900",
    header: "bg-white/70 backdrop-blur-2xl border-b border-blue-200/60 text-blue-950 shadow-sm shadow-blue-500/5",
    sidebar: "bg-gradient-to-b from-blue-50 via-indigo-50 to-sky-50 text-blue-900 border-r border-blue-200/70 shadow-[2px_0_15px_-3px_rgba(59,130,246,0.15)]",
    input: "bg-white/90 border-blue-300 text-blue-950 placeholder:text-blue-500 focus:border-blue-400 focus:ring-2 focus:ring-blue-400/30 rounded-lg transition-all duration-200 shadow-inner",
    muted: "text-blue-600 text-sm font-medium tracking-wide",
    buttonPrimary: "bg-gradient-to-r from-blue-400 to-indigo-500 text-white hover:from-blue-500 hover:to-indigo-600 px-4 py-2 rounded-lg font-semibold shadow-md shadow-blue-400/30 transition-all duration-200 active:scale-[0.98] hover:shadow-lg",
    buttonSecondary: "bg-blue-50/80 border border-blue-300 text-blue-700 hover:bg-blue-100 hover:border-blue-400 px-4 py-2 rounded-lg font-medium transition-all duration-200 active:scale-[0.98]",
    card: "bg-white/85 backdrop-blur-md border border-blue-200/70 rounded-xl shadow-xl shadow-blue-500/10",
    badge: "bg-blue-100 text-blue-800 px-2.5 py-0.5 rounded-full text-xs font-semibold border border-blue-300/60",
    divider: "border-blue-200/70",
    accent: "text-blue-500",
    link: "text-blue-600 hover:text-blue-700 font-medium transition-colors",
  },
};

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "light";
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved && themes[saved] ? saved : "light";
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, theme);
    
    if (typeof window !== "undefined") {
      const body = document.body;
      
      // Përdorim ngjyra solide që nuk bllokohen nga Tailwind
      const bgColors = {
        light: "bg-white",
        dark: "bg-[#0A0F1C]",
        blue: "bg-blue-100",  // Ndryshimi kryesor për të dalluar "Light Blue"
        ocean: "bg-cyan-100",
        frost: "bg-blue-50"
      };
      
      body.className = body.className
        .replace(/bg-[\w-]+/g, '')
        .replace(/from-[\w-]+/g, '')
        .replace(/via-[\w-]+/g, '')
        .replace(/to-[\w-]+/g, '')
        .replace(/bg-\[.*?\]/g, '')
        .trim();
      
      if (bgColors[theme]) {
        body.classList.add(bgColors[theme]);
      }
      
      body.style.transition = 'background-color 0.3s ease, background-image 0.3s ease';
    }
  }, [theme]);

  const value = useMemo(() => {
    const themeKeys = Object.keys(themes);
    return {
      theme,
      setTheme,
      currentTheme: themes[theme] || themes.light,
      isLight: theme === 'light',
      isDark: theme === 'dark',
      isBlue: theme === 'blue', // Ndryshuar nga isSky në isBlue
      isOcean: theme === 'ocean',
      isFrost: theme === 'frost',
      toggleTheme: () => {
        const nextIndex = (themeKeys.indexOf(theme) + 1) % themeKeys.length;
        setTheme(themeKeys[nextIndex]);
      },
      availableThemes: themeKeys
    };
  }, [theme]);

  return (
    <ThemeContext.Provider value={value}>
      <div className={`theme-${theme} transition-all duration-300`}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
}

export function usePanelTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("usePanelTheme must be used within ThemeProvider");
  }
  return context;
}

export function useThemeClasses() {
  const { currentTheme } = usePanelTheme();
  return currentTheme;
}